<template>
  <wz-card :title="entry.roleTitle ? `${entry.companyName} · ${entry.roleTitle}` : entry.companyName">
    <wz-alert v-if="entry.problem" variant="danger" class="mb-3">{{ entry.problem }}</wz-alert>

    <!-- Lo que necesita para poder firmar. Se vuelve a abrir si el correo deja de servir o quiere cambiar la imagen. -->
    <template v-if="setupOpen">
      <div class="mb-3">
        <strong>Valida tu correo</strong>
        <p class="text-muted text-sm mb-2">
          Tiene que ser un correo personal: uno de la empresa no sirve, porque alguien más puede leerlo. Ahí te llegarán los
          códigos para firmar. Vale para todas las empresas donde firmas.
        </p>
        <div class="flex flex-wrap items-end gap-2">
          <template v-if="!contactSentTo">
            <div class="flex-1" style="min-width: 240px;">
              <wz-input v-model="contactValue" label="Correo personal" />
            </div>
            <wz-button variant="primary" class="mb-1" :disabled="!(contactValue || '').trim()" @click="requestContact">Enviar código</wz-button>
          </template>
          <template v-else>
            <div class="flex-1" style="min-width: 240px;">
              <wz-input v-model="contactCode" :label="`Código enviado a ${contactSentTo}`" />
            </div>
            <wz-button variant="primary" class="mb-1" :disabled="!(contactCode || '').trim()" @click="confirmContact">Confirmar</wz-button>
            <wz-button variant="light" class="mb-1" @click="contactSentTo = null">Usar otro correo</wz-button>
          </template>
        </div>
      </div>

      <div class="mb-3">
        <strong>Sube la imagen de tu firma</strong>
        <p class="text-muted text-sm mb-2">PNG o JPG de hasta 500 KB. Vale para todas las empresas donde firmas.</p>
        <wz-file-input :id-file="`signer-image-${entry.signerId}`" :accept-file="['.png', '.jpg', '.jpeg']" @onChange="uploadImage" />
      </div>
    </template>

    <p class="text-sm mb-3">
      <wz-badge :variant="entry.contactValidated ? 'success' : 'warning'" pill class="mr-1">
        {{ entry.contactValidated ? `Correo validado: ${entry.contact}` : 'Correo sin validar' }}
      </wz-badge>
      <wz-badge :variant="entry.hasSignatureImage ? 'success' : 'warning'" pill class="mr-1">
        {{ entry.hasSignatureImage ? 'Imagen de firma cargada' : 'Sin imagen de firma' }}
      </wz-badge>
      <a v-if="setupComplete" href="#" @click.prevent="setupShown = !setupShown">{{ setupShown ? 'Ocultar' : 'Cambiar correo o imagen' }}</a>
    </p>

    <!-- Lotes ya autorizados: se firman en segundo plano y salen de la lista de abajo mientras tanto. -->
    <p v-if="batchesError" class="text-muted text-sm mb-2">No se pudo actualizar el estado de los lotes.</p>
    <div v-if="batches.length" class="mb-3">
      <strong>Lotes autorizados en los últimos 7 días</strong>
      <wz-alert v-for="lot in batches" :key="lot.authorizationId" :variant="lotVariant(lot)" class="mt-2">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span>
            <strong>{{ lot.signed }} de {{ lot.total }} firmados</strong>
            · autorizado el {{ formatDateTime(lot.authorizedAt) }}
            <span v-if="isRunning(lot)"> · {{ lot.queued }} en cola</span>
            <span v-if="lot.failed"> · {{ lot.failed }} con error</span>
          </span>
          <wz-button v-if="canRetry(lot)" variant="primary" size="sm" :disabled="retrying === lot.authorizationId" @click="retry(lot)">Reintentar</wz-button>
        </div>
        <div v-if="signedElsewhere(lot)" class="text-sm mt-1">{{ signedElsewhere(lot) }} se firmaron en otro lote.</div>
        <div v-if="isRunning(lot)" class="text-sm mt-1">
          <template v-if="stalled[lot.authorizationId]">Lleva un rato sin avanzar. Puede haberse detenido: usa Reintentar, no hace falta otro código.</template>
          <template v-else>Puedes salir de esta página: la firma sigue en segundo plano.</template>
        </div>
        <div v-if="lot.expired && (lot.queued || lot.failed)" class="text-sm mt-1">
          Esta autorización venció: lo que faltaba volvió a la lista y se firma con un código nuevo.
        </div>
        <ul v-if="lot.failures && lot.failures.length" class="mt-1 mb-0 text-sm">
          <li v-for="f in lot.failures" :key="f.documentId">{{ f.documentTypeName }} de {{ f.employeeName }}: {{ f.reason }}</li>
        </ul>
      </wz-alert>
    </div>

    <wz-table v-if="entry.documents.length">
      <thead>
        <tr>
          <th><input type="checkbox" :checked="allSelected" :disabled="!selectable.length" aria-label="Seleccionar todos" @change="toggleAll($event.target.checked)"></th>
          <th>Documento</th>
          <th>Colaborador</th>
          <th>Contrato</th>
          <th>Fecha</th>
          <th>Archivo</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="d in entry.documents" :key="d.documentId">
          <td><input v-model="selected" type="checkbox" :value="d.documentId" :disabled="!!d.blockedReason" :aria-label="`${d.documentTypeName} de ${d.employeeName}`"></td>
          <td>
            {{ d.documentTypeName }}
            <div v-if="d.blockedReason" class="text-sm" style="color: var(--admin-danger, #dc3545);">{{ d.blockedReason }}</div>
          </td>
          <td>{{ d.employeeName }}</td>
          <td>{{ d.contractNumber || '—' }}</td>
          <td>{{ formatDate(d.date) }}</td>
          <td><a href="#" @click.prevent="review(d)">Revisar</a></td>
        </tr>
      </tbody>
    </wz-table>
    <p v-else class="mb-0 text-muted">Nada por firmar.</p>

    <template v-if="entry.documents.length">
      <wz-alert v-if="selected.length > maxBatch" variant="warning" class="mt-3">
        Elegiste {{ selected.length }} documentos y un lote admite hasta {{ maxBatch }}. Quita algunos para continuar.
        <a href="#" @click.prevent="keepFirst">Dejar solo los primeros {{ maxBatch }}</a>
      </wz-alert>
      <p v-if="!entry.contactValidated" class="text-muted text-sm mt-3 mb-0">Valida tu correo para poder firmar.</p>
      <div class="flex justify-end mt-3">
        <wz-button variant="primary" :disabled="!canSign" @click="openBatch">Firmar seleccionados ({{ selected.length }})</wz-button>
      </div>
    </template>

    <wz-modal v-model="batch.show" title="Firmar seleccionados" :close-on-overlay="false">
      <wz-alert variant="warning" class="mb-3">
        <strong>Un solo código autoriza los {{ batch.docs.length }} documento(s) de esta lista.</strong>
        Cada uno queda firmado con tu nombre y su propio expediente. Si uno falla, los demás se firman igual.
      </wz-alert>
      <ul class="mb-3 text-sm" style="max-height: 200px; overflow-y: auto;">
        <li v-for="d in batch.docs" :key="d.documentId">{{ docLabel(d) }}</li>
      </ul>
      <template v-if="batch.sentTo">
        <p class="text-sm mb-2">Te enviamos el código a <strong>{{ batch.sentTo }}</strong>. Vence en 10 minutos.</p>
        <wz-input v-model="batch.code" label="Código" />
      </template>
      <wz-alert v-if="batch.error" variant="danger" class="mt-3">{{ batch.error }}</wz-alert>
      <template #footer>
        <wz-button variant="outline" :disabled="batch.busy" @click="batch.show = false">Cancelar</wz-button>
        <wz-button v-if="!batch.sentTo" variant="primary" :disabled="batch.busy" @click="requestBatch">Enviar código</wz-button>
        <template v-else>
          <wz-button variant="light" :disabled="batch.busy" @click="requestBatch">Pedir otro código</wz-button>
          <wz-button variant="primary" :disabled="batch.busy || !(batch.code || '').trim()" @click="confirmBatch">Firmar {{ batch.docs.length }} documento(s)</wz-button>
        </template>
      </template>
    </wz-modal>
  </wz-card>
</template>

<script>
import { Alert, Util } from '@wizepe/wizeui'
import fileDownload from 'js-file-download'
import { LegajoService, SignatureService } from '@/core/services'

const MAX_BATCH = 200
const MAX_IMAGE_BYTES = 500000
const POLL_MS = 5000
// Sin cambios durante este tiempo, el lote se da por detenido. Una sola consulta no alcanza: la cola tiene un solo hilo, compartido
// con la generación de legajos, y es normal que pasen varias consultas entre un documento y otro.
const STALL_MS = 30000

const emptyBatch = () => ({ show: false, docs: [], sentTo: null, code: '', error: null, busy: false })

export default {
  name: 'SignerInboxCard',
  props: {
    entry: { type: Object, required: true },
  },
  data() {
    return {
      maxBatch: MAX_BATCH,
      contactValue: '',
      contactCode: '',
      contactSentTo: null,
      setupShown: false,
      selected: [],
      // El código autoriza exactamente estos documentos con estos hashes: la lista se congela al abrir el modal.
      batch: emptyBatch(),
      batches: [],
      batchesError: false,
      // Lotes que llevan STALL_MS sin avanzar, por authorizationId.
      stalled: {},
      retrying: null,
    }
  },
  computed: {
    setupComplete() { return this.entry.contactValidated && this.entry.hasSignatureImage },
    setupOpen() { return !this.setupComplete || this.setupShown },
    selectable() { return this.entry.documents.filter(d => !d.blockedReason) },
    allSelected() { return this.selectable.length > 0 && this.selected.length === this.selectable.length },
    canSign() { return this.entry.contactValidated && this.selected.length > 0 && this.selected.length <= MAX_BATCH },
  },
  watch: {
    // Lo ya firmado o bloqueado tras recargar no sigue seleccionado.
    entry() {
      const ids = this.selectable.map(d => d.documentId)
      this.selected = this.selected.filter(id => ids.includes(id))
    },
  },
  created() {
    // Sin reactividad a propósito: es control de la consulta periódica, no se pinta.
    this.pollTimer = null
    this.stopped = false
    this.running = new Set()
    this.progress = {}
    this.loadBatches()
  },
  beforeDestroy() {
    this.stopped = true
    clearTimeout(this.pollTimer)
  },
  methods: {
    isRunning(lot) {
      return lot.queued > 0 && !lot.expired
    },
    canRetry(lot) {
      return !lot.expired && (lot.failed > 0 || !!this.stalled[lot.authorizationId])
    },
    // Un documento del lote que se firmó por otro lote no cuenta como firmado, en cola ni fallido en este.
    signedElsewhere(lot) {
      return lot.total - lot.signed - lot.queued - lot.failed
    },
    lotVariant(lot) {
      if (lot.failed || this.stalled[lot.authorizationId] || (lot.expired && lot.queued)) return 'warning'
      return this.isRunning(lot) ? 'info' : 'success'
    },
    async loadBatches() {
      try {
        const lots = (await SignatureService.batches(this.entry.signerId)).payload || []
        if (this.stopped) return
        this.batchesError = false
        this.applyBatches(lots)
      } catch (e) {
        if (this.stopped) return
        this.batchesError = true
      }
      clearTimeout(this.pollTimer)
      if (this.running.size) this.pollTimer = setTimeout(this.loadBatches, POLL_MS)
    },
    applyBatches(lots) {
      const now = Date.now()
      const running = new Set()
      const progress = {}
      const stalled = {}
      lots.filter(this.isRunning).forEach(lot => {
        const id = lot.authorizationId
        const mark = `${lot.signed}/${lot.queued}/${lot.failed}`
        const seen = this.progress[id]
        const since = seen && seen.mark === mark ? seen.since : now
        running.add(id)
        progress[id] = { mark, since }
        if (now - since >= STALL_MS) stalled[id] = true
      })
      const finished = [...this.running].some(id => !running.has(id))
      this.running = running
      this.progress = progress
      this.stalled = stalled
      this.batches = lots
      if (finished) this.$emit('reload')
    },
    async retry(lot) {
      const id = lot.authorizationId
      this.retrying = id
      try {
        const queued = (await SignatureService.retryBatch(id, this.entry.signerId)).payload.queued
        delete this.progress[id]
        this.running.add(id)
        Alert.success(`${queued} documento(s) otra vez en cola`)
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo reintentar')
        this.$emit('reload')
      } finally {
        this.retrying = null
      }
      await this.loadBatches()
    },
    toggleAll(checked) {
      this.selected = checked ? this.selectable.map(d => d.documentId) : []
    },
    keepFirst() {
      this.selected = this.selected.slice(0, MAX_BATCH)
    },
    docLabel(d) {
      return `${d.documentTypeName} de ${d.employeeName}`
    },
    async requestContact() {
      try {
        const r = (await SignatureService.requestContact(this.entry.signerId, this.contactValue.trim())).payload
        this.contactSentTo = r.sentTo
        this.contactCode = ''
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo enviar el código')
      }
    },
    async confirmContact() {
      try {
        await SignatureService.confirmContact(this.entry.signerId, this.contactCode.trim())
        this.contactSentTo = null
        this.contactValue = ''
        this.contactCode = ''
        this.setupShown = false
        Alert.success('Correo validado')
        this.$emit('reload')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo validar el correo')
      }
    },
    async uploadImage(files) {
      const file = files && files[0]
      if (!file) return
      if (file.size > MAX_IMAGE_BYTES) {
        Alert.error('La imagen no debe superar 500 KB')
        return
      }
      try {
        await SignatureService.uploadImage(this.entry.signerId, file)
        this.setupShown = false
        Alert.success('Imagen de firma guardada')
        this.$emit('reload')
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'No se pudo guardar la imagen')
      }
    },
    async review(d) {
      try {
        const resp = await LegajoService.download(d.documentId)
        fileDownload(resp.data, Util.getFileName(resp) || 'documento.pdf')
      } catch (e) {
        Alert.error('El archivo no está disponible')
      }
    },
    openBatch() {
      this.batch = { ...emptyBatch(), show: true, docs: this.entry.documents.filter(d => this.selected.includes(d.documentId)) }
    },
    batchItems() {
      return this.batch.docs.map(d => ({ documentId: d.documentId, documentHash: d.documentHash }))
    },
    async requestBatch() {
      this.batch.busy = true
      this.batch.error = null
      try {
        this.batch.sentTo = (await SignatureService.requestBatch(this.entry.signerId, this.batchItems())).payload.sentTo
        this.batch.code = ''
      } catch (e) {
        this.batch.error = e.errors ? e.errors.message : 'No se pudo enviar el código'
        // Sin código en curso, lo más probable es que un documento haya cambiado: la lista se actualiza para que lo veas.
        if (!this.batch.sentTo) this.$emit('reload')
      } finally {
        this.batch.busy = false
      }
    },
    async confirmBatch() {
      this.batch.busy = true
      this.batch.error = null
      try {
        const r = (await SignatureService.confirmBatch(this.entry.signerId, this.batchItems(), this.batch.code.trim())).payload
        this.batch = emptyBatch()
        this.selected = []
        // Se marca antes de consultar: un lote corto puede terminar antes de la primera consulta y la lista igual tiene que recargarse.
        this.running.add(r.authorizationId)
        Alert.success(`Autorizado: ${r.documents} documento(s) se están firmando. Puedes salir de esta página.`)
        this.$emit('reload')
        this.loadBatches()
      } catch (e) {
        this.batch.error = e.errors ? e.errors.message : 'No se pudo firmar'
      } finally {
        this.batch.busy = false
      }
    },
    formatDateTime(d) {
      if (!d) return '—'
      return `${this.formatDate(d)} ${String(d).substring(11, 16)}`.trim()
    },
    formatDate(d) {
      if (!d) return '—'
      const parts = String(d).substring(0, 10).split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : d
    },
  },
}
</script>
